import * as React from 'react';

/**
 * Extracted from create-offer-page.tsx (CU-86cbc2qv8, offer-client-collaboration)
 * so the quick-add-offer-picker's "creating" mode can reuse the same
 * brand-select + inline-client-create merge logic instead of a parallel
 * implementation. Kept form-library-agnostic (no RHF dependency) — callers
 * wire the returned `clients`/`handleClientCreated` into their own form's
 * `Controller`/`setValue` calls.
 *
 * Generic over `T` (rather than a fixed shape) so the full create-offer form
 * can pass real `Client[]` (numeric `id`) while the quick-add picker — which
 * only has `{label, value, brands, acronym}` option data, not full `Client`
 * records — passes its own minimal shape; both only need an `id` to merge on.
 */
export interface ClientOption {
  id: string;
  name: string;
  brands: string[];
  /** The client's acronym — feeds the campaign-number preview (offer-draft-numbering). */
  acronym: string;
}

/**
 * Merges a base client list (e.g. from `useClientsList()`) with locally
 * created/selected clients so a newly created client is available for
 * immediate selection without waiting on a query refetch round-trip.
 */
export function useInlineClientCreate<T extends { id: string | number }>(baseClients: T[]) {
  const [createdClients, setCreatedClients] = React.useState<T[]>([]);

  const clients = React.useMemo(() => {
    const byId = new Map(baseClients.map((client) => [client.id, client]));
    for (const client of createdClients) byId.set(client.id, client);
    return [...byId.values()];
  }, [baseClients, createdClients]);

  const handleClientCreated = React.useCallback((client: T) => {
    setCreatedClients((current) => [...current.filter((item) => item.id !== client.id), client]);
  }, []);

  return { clients, handleClientCreated };
}

/** True when `brand` is unset, or still present in `clientBrands`. */
export function isBrandStillValid(
  brand: string | null | undefined,
  clientBrands: string[] | undefined,
): boolean {
  if (!brand) return true;
  return (clientBrands ?? []).includes(brand);
}

/**
 * Auto-selects the brand for a client just created inline from an
 * offer-creation form (CU-86cbc2qv8) — only when it has exactly one brand,
 * since that's the one case where the choice isn't ambiguous. Returns
 * `null` for zero or multiple brands, leaving the field for manual choice.
 * Callers MUST only apply this on the inline-create path (`ClientDialog`'s
 * `onSuccess`), never when the user merely selected an *existing* client
 * from suggestions (`onSelectExisting`) or from the picker's own list.
 */
export function resolveAutoSelectedBrand(brands: string[]): string | null {
  return brands.length === 1 ? (brands[0] ?? null) : null;
}

/**
 * Applies `resolveAutoSelectedBrand`'s decision through the given
 * `setBrand` callback (an RHF `setValue('brand', ...)` call in practice) —
 * shared by create-offer-page.tsx and quick-add-offer-picker-form.ts so
 * neither duplicates the wasCreated-gated call. No-op when `wasCreated` is
 * false (an existing client was selected from suggestions, not created).
 */
export function applyAutoSelectedBrand(
  brands: string[],
  wasCreated: boolean,
  setBrand: (brand: string) => void,
): void {
  if (!wasCreated) return;
  const brand = resolveAutoSelectedBrand(brands);
  if (brand) setBrand(brand);
}
